/*
 * Journey step: measure your space.
 *
 * Produces the normalised shape every later step reads:
 *
 *   {
 *     area, areaLabel, cover, length, width, height,
 *     ends, end1, end2,
 *     spans:     [{ label, run, drop, area }],
 *     totalArea, allowancePct, totalAreaAllowed
 *   }
 *
 * "spans" is the important part. A netting roll has to physically reach across
 * a span, so the netting step matches against each span rather than against a
 * single square-foot total. An enclosed end is its own span.
 */
(function () {
  'use strict';

  var STEP_FALLBACK = 'measure';

  function round(n, dp) {
    var f = Math.pow(10, dp === undefined ? 2 : dp);
    return Math.round(n * f) / f;
  }

  function num(input) {
    if (!input) return null;
    var v = parseFloat(input.value);
    return isFinite(v) && v > 0 ? v : null;
  }

  function Measure(root) {
    this.root = root;
    this.config = this.readConfig();
    this.stepId = (this.config && this.config.stepId) || STEP_FALLBACK;

    // Whatever the store sells in. Every number this step SHOWS goes
    // through it, including the summary and the fenceline height buttons,
    // which is where a stray 'm' survived the first pass.
    this.unit = (this.config && this.config.units) === 'm' ? 'm' : 'ft';

    this.areaButtons = root.querySelectorAll('[data-measure-area]');
    this.form = root.querySelector('[data-measure-form]');
    this.formHeading = root.querySelector('[data-measure-form-heading]');
    this.diagram = root.querySelector('[data-measure-diagram]');
    this.summary = root.querySelector('[data-measure-summary]');
    this.spansList = root.querySelector('[data-measure-spans]');
    this.totalEl = root.querySelector('[data-measure-total]');
    this.errorEl = root.querySelector('[data-measure-error]');
    this.endsSelect = root.querySelector('[name="ends"]');
    this.heightChoicesEl = root.querySelector('[data-measure-height-choices]');

    this.area = null;
    this.journey = null;

    this.bind();
  }

  Measure.prototype.readConfig = function () {
    var el = this.root.querySelector('[data-measure-config]');
    if (!el) return null;

    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      return null;
    }
  };

  Measure.prototype.areaConfig = function (id) {
    if (!this.config || !this.config.areas) return null;

    for (var i = 0; i < this.config.areas.length; i++) {
      if (this.config.areas[i].id === id) return this.config.areas[i];
    }
    return null;
  };

  Measure.prototype.field = function (name) {
    return this.root.querySelector('[data-measure-field="' + name + '"]');
  };

  Measure.prototype.input = function (name) {
    return this.root.querySelector('[name="' + name + '"]');
  };

  Measure.prototype.bind = function () {
    var self = this;

    for (var i = 0; i < this.areaButtons.length; i++) {
      this.areaButtons[i].addEventListener('click', function (event) {
        self.selectArea(event.currentTarget.getAttribute('data-measure-area'));
      });
    }

    this.root.addEventListener('input', function (event) {
      if (event.target.matches('input[type="number"]')) self.evaluate();
    });

    this.root.addEventListener('change', function (event) {
      if (event.target === self.endsSelect) {
        self.renderEnds();
        self.evaluate();
      }
    });

    // Fixed-height buttons. The visible control writes into the height input,
    // which stays the single source of truth so collect() and restore() never
    // care whether the height was typed or picked.
    this.root.addEventListener('click', function (event) {
      if (!event.target || !event.target.closest) return;
      var btn = event.target.closest('[data-measure-height-option]');
      if (!btn) return;

      var heightInput = self.input('height');
      if (heightInput) heightInput.value = btn.getAttribute('data-measure-height-option');
      self.syncHeightChoices();
      self.evaluate();
    });

    // The journey may boot before or after this script. Handle both.
    if (window.CatnetsJourney) {
      this.attach(window.CatnetsJourney);
    } else {
      document.addEventListener('journey:ready', function (event) {
        self.attach(event.detail.journey);
      });
    }
  };

  Measure.prototype.attach = function (journey) {
    this.journey = journey;
    this.restore();
  };

  /** Repopulate from saved state so returning customers are not re-typing. */
  Measure.prototype.restore = function () {
    if (!this.journey) return;

    var data = this.journey.data(this.stepId);
    if (!data || !data.area) return;

    this.selectArea(data.area, true);

    var pairs = [
      ['length', data.length],
      ['width', data.width],
      ['height', data.height]
    ];

    for (var i = 0; i < pairs.length; i++) {
      var input = this.input(pairs[i][0]);
      if (input && pairs[i][1]) input.value = pairs[i][1];
    }

    if (this.endsSelect && data.ends) {
      this.endsSelect.value = data.ends;
      this.renderEnds();
    }

    [1, 2].forEach(function (n) {
      var end = data['end' + n];
      if (!end) return;

      var w = this.input('end' + n + '_width');
      var h = this.input('end' + n + '_height');
      if (w && end.width) w.value = end.width;
      if (h && end.height) h.value = end.height;
    }, this);

    // Restored values landed after selectArea ran, so re-sync the height
    // select to show the saved choice.
    this.syncHeightChoices();

    this.evaluate();
  };

  Measure.prototype.selectArea = function (id, quiet) {
    var config = this.areaConfig(id);
    if (!config) return;

    this.area = id;

    for (var i = 0; i < this.areaButtons.length; i++) {
      var btn = this.areaButtons[i];
      var on = btn.getAttribute('data-measure-area') === id;
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
      btn.classList.toggle('is-selected', on);
    }

    var button = this.root.querySelector('[data-measure-area="' + id + '"]');
    var needs = (button.getAttribute('data-needs') || '').split(' ').filter(Boolean);

    ['length', 'width', 'height'].forEach(function (name) {
      var field = this.field(name);
      if (!field) return;

      var wanted = needs.indexOf(name) !== -1;
      field.hidden = !wanted;

      // Clear what does not apply, so a switch between areas cannot leave a
      // stale measurement feeding the maths.
      if (!wanted) {
        var input = this.input(name);
        if (input) input.value = '';
      }
    }, this);

    var endsField = this.field('ends');
    if (endsField) {
      endsField.hidden = !config.allowsEnds;
      if (!config.allowsEnds && this.endsSelect) this.endsSelect.value = 'none';
    }
    this.renderEnds();

    this.syncHeightChoices();

    if (this.formHeading) this.formHeading.textContent = config.formHeading || config.label;

    // Help lines can differ per area ("house to fence" is right for an alley,
    // wrong for a backyard). Area override wins, section default otherwise;
    // empty help hides via CSS.
    var defaults = (this.config && this.config.helpDefaults) || {};
    var overrides = config.help || {};
    ['width', 'height', 'length'].forEach(function (name) {
      var el = this.root.querySelector('[data-measure-help="' + name + '"]');
      if (el) el.textContent = overrides[name] || defaults[name] || '';
    }, this);

    if (this.diagram) {
      if (config.diagram) {
        this.diagram.src = config.diagram;
        this.diagram.alt = config.diagramAlt || '';
        this.diagram.hidden = false;
      } else {
        this.diagram.hidden = true;
      }
    }

    this.form.hidden = false;

    if (!quiet) this.evaluate();
  };

  /**
   * Some areas only work at fixed netting heights (fenceline brackets suit
   * 1.2m or 1.8m), declared per area as comma-separated choices. When set,
   * side-by-side buttons replace the free height input; the hidden input
   * keeps holding the value so the rest of the step never knows the
   * difference.
   */
  Measure.prototype.syncHeightChoices = function () {
    if (!this.heightChoicesEl) return;

    var config = this.areaConfig(this.area) || {};
    var choices = String(config.heightChoices || '')
      .split(',')
      .map(function (c) { return c.trim(); })
      .filter(Boolean);

    var input = this.input('height');
    var wrap = input ? input.parentNode : null;

    if (!choices.length) {
      this.heightChoicesEl.hidden = true;
      if (wrap) wrap.hidden = false;
      return;
    }

    // A previously typed height that is not one of the choices cannot stand.
    if (input && choices.indexOf(input.value) === -1) input.value = '';

    /*
     * With only one height on offer there is no choice to make, so do not make
     * the customer make it. A lone unpicked button reads as a decision and
     * silently blocks Continue until it is clicked, which is a dead end that
     * looks like a bug. Pre-select it instead; it still renders as a selected
     * button, so what they are getting is on screen.
     */
    if (input && !input.value && choices.length === 1) input.value = choices[0];

    var current = input ? input.value : '';

    var html = '';
    for (var i = 0; i < choices.length; i++) {
      var selected = choices[i] === current;
      html +=
        '<button type="button" class="jmeasure__choice' + (selected ? ' is-selected' : '') + '"' +
        ' data-measure-height-option="' + choices[i] + '"' +
        ' aria-pressed="' + (selected ? 'true' : 'false') + '">' +
        choices[i] + this.unit + '</button>';
    }
    this.heightChoicesEl.innerHTML = html;

    this.heightChoicesEl.hidden = false;
    if (wrap) wrap.hidden = true;
  };

  Measure.prototype.renderEnds = function () {
    var config = this.areaConfig(this.area);
    var allows = config && config.allowsEnds;
    var value = allows && this.endsSelect ? this.endsSelect.value : 'none';
    var count = value === 'two' ? 2 : value === 'one' ? 1 : 0;

    [1, 2].forEach(function (n) {
      var el = this.root.querySelector('[data-measure-end="' + n + '"]');
      if (!el) return;

      var show = n <= count;
      el.hidden = !show;

      if (!show) {
        var w = this.input('end' + n + '_width');
        var h = this.input('end' + n + '_height');
        if (w) w.value = '';
        if (h) h.value = '';
      }
    }, this);
  };

  /**
   * @returns {{ok: boolean, message: string|null, data: object|null}}
   */
  Measure.prototype.collect = function () {
    var config = this.areaConfig(this.area);
    if (!config) return { ok: false, message: null, data: null };

    var button = this.root.querySelector('[data-measure-area="' + this.area + '"]');
    var needs = (button.getAttribute('data-needs') || '').split(' ').filter(Boolean);

    var values = { length: null, width: null, height: null };
    var missing = false;

    for (var i = 0; i < needs.length; i++) {
      var v = num(this.input(needs[i]));
      if (v === null) missing = true;
      values[needs[i]] = v;
    }

    if (missing) return { ok: false, message: null, data: null };

    var run = values.length;
    var drop = config.cover === 'height' ? values.height : values.width;

    if (!run || !drop) return { ok: false, message: null, data: null };

    var spans = [{ label: 'Main run', run: run, drop: drop, area: round(run * drop) }];

    var ends = config.allowsEnds && this.endsSelect ? this.endsSelect.value : 'none';
    var count = ends === 'two' ? 2 : ends === 'one' ? 1 : 0;
    var endData = { end1: null, end2: null };

    for (var n = 1; n <= count; n++) {
      var ew = num(this.input('end' + n + '_width'));
      var eh = num(this.input('end' + n + '_height'));

      if (ew === null || eh === null) return { ok: false, message: null, data: null };

      endData['end' + n] = { width: ew, height: eh };
      spans.push({ label: 'End ' + n, run: ew, drop: eh, area: round(ew * eh) });
    }

    var total = 0;
    for (var sp = 0; sp < spans.length; sp++) total += spans[sp].area;

    var allowance = this.config && typeof this.config.allowancePct === 'number'
      ? this.config.allowancePct
      : 10;

    return {
      ok: true,
      message: null,
      data: {
        area: this.area,
        areaLabel: config.label,
        cover: config.cover,
        length: values.length,
        width: values.width,
        height: values.height,
        ends: ends,
        end1: endData.end1,
        end2: endData.end2,
        spans: spans,
        totalArea: round(total),
        allowancePct: allowance,
        totalAreaAllowed: round(total * (1 + allowance / 100))
      }
    };
  };

  Measure.prototype.evaluate = function () {
    var result = this.collect();

    this.setError(result.message);
    this.renderSummary(result.ok ? result.data : null);

    if (!this.journey) return;

    if (result.ok) {
      this.journey.complete(this.stepId, result.data);
    } else if (this.journey.data(this.stepId)) {
      // Was valid, is no longer. Drop it so the customer cannot continue on
      // stale measurements.
      this.journey.clear(this.stepId);
    }
  };

  Measure.prototype.setError = function (message) {
    if (!this.errorEl) return;

    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  };

  Measure.prototype.renderSummary = function (data) {
    if (!this.summary) return;

    if (!data) {
      this.summary.hidden = true;
      return;
    }

    if (this.spansList) {
      var html = '';
      var unit = this.unit;
      for (var i = 0; i < data.spans.length; i++) {
        var s = data.spans[i];
        html +=
          '<li><span class="jmeasure__span-label">' + s.label + '</span>' +
          '<span class="jmeasure__span-size">' + s.run + unit + ' &times; ' + s.drop + unit + '</span></li>';
      }
      this.spansList.innerHTML = html;
    }

    if (this.totalEl) this.totalEl.textContent = data.totalAreaAllowed;

    this.summary.hidden = false;
  };

  function boot() {
    var roots = document.querySelectorAll('[data-journey-step-source]');

    for (var i = 0; i < roots.length; i++) {
      if (roots[i].querySelector('[data-measure-config]')) new Measure(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
